"use client";

import type { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import type { UnderwritingEngineProps } from "./underwriting-engine";
import {
  UnderwritingEngineBox,
  UnderwritingEngineCard,
  UnderwritingEngineGlass,
} from "./underwriting-engine-card";
import type { EngineGlassVariant } from "./underwriting-engine-card";
import { EngineHeading, EnginePanel } from "./underwriting-engine-chrome";
import { Waveform, MOBILE_TICK_COUNT } from "./underwriting-engine-waveform";

// Mobile layer stack geometry — proportions match desktop (underwriting-engine-visual)
// scaled to STACK_W.
const STACK_W = 300;
const DESKTOP_STACK_W = 340;
const SCALE = STACK_W / DESKTOP_STACK_W;
// UnderwritingEngineBox is authored at 340×286; scale uniformly from top-left.
const BOX_NATIVE_W = DESKTOP_STACK_W;
const BOX_NATIVE_H = 286;
const BOX_H = Math.round(BOX_NATIVE_H * SCALE);
const CARD_H = Math.round(STACK_W * (174 / 337)); // ≈ 155
// Desktop layer resting offsets: 0 / 46 / 92 px at 340px — keep the ratio.
const LAYER_OFFSETS = [0, 46, 92].map((px) => Math.round(px * SCALE));
const TOP_ROOM = 20;
const BOX_OVERHANG = Math.max(0, BOX_H - CARD_H - LAYER_OFFSETS[2]!);
const STACK_H = LAYER_OFFSETS[2]! + CARD_H + TOP_ROOM + BOX_OVERHANG;
const DROP = -280; // start above; clipped by the panel

// Step carousel — Figma mobile (6399:29295 / 7627:51962): horizontal slide
// with next card peeking from the right at ~90.5% scale, text hidden until active.
const PREVIEW_SCALE = 0.905;

const SPRING = { stiffness: 90, damping: 28, mass: 0.4 } as const;

// Transparent glass sheets interleaved with colour layers — same positions as
// desktop (underwriting-engine-visual GLASS), scaled to mobile STACK_W.
const GLASS: {
  variant: EngineGlassVariant;
  bottom: number;
  z: number;
  tint: string;
}[] = [
  {
    variant: "mid",
    bottom: Math.round(24 * SCALE),
    z: 15,
    tint: "linear-gradient(45deg, rgba(255,96,64,0.3), rgba(201,74,214,0.24))",
  },
  {
    variant: "mid",
    bottom: Math.round(70 * SCALE),
    z: 25,
    tint: "linear-gradient(45deg, rgba(201,74,214,0.3), rgba(0,120,255,0.24))",
  },
  {
    variant: "top",
    bottom: Math.round(116 * SCALE),
    z: 35,
    tint: "linear-gradient(45deg, rgba(0,120,255,0.3), rgba(64,208,255,0.24))",
  },
];

/**
 * Map carousel position → scrubber fill 0→1 in equal thirds:
 * step 1 → 1/3, step 2 → 2/3, step 3 → 1.
 */
function stepScrubberProgress(
  api: EmblaCarouselType,
  stepCount: number
): number {
  const snaps = api.scrollSnapList();
  const count = stepCount > 0 ? stepCount : snaps.length;
  if (count <= 1) return 1;

  const scrollProgress = api.scrollProgress();
  const selected = api.selectedScrollSnap();

  for (let i = 0; i < snaps.length - 1; i++) {
    const start = snaps[i]!;
    const end = snaps[i + 1]!;
    if (scrollProgress >= start - 0.001 && scrollProgress <= end + 0.001) {
      const span = end - start || 1;
      const t = (scrollProgress - start) / span;
      return Math.min(1, Math.max(0, (i + t + 1) / count));
    }
  }

  return (selected + 1) / count;
}

/** Signed distance from scroll position to a snap. */
function diffToSnap(snapProgress: number, scrollProgress: number) {
  return snapProgress - scrollProgress;
}

/**
 * Mobile view: a swipeable carousel of step cards. Swiping drives BOTH the
 * scrubber progress bar AND the layer reveals — the layers are not scroll-based
 * here, they appear one by one as the carousel advances.
 */
export function UnderwritingEngineMobile({
  heading,
  subheading,
  steps,
  className,
}: UnderwritingEngineProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: false,
  });
  const progressTarget = useMotionValue(
    steps.length > 0 ? 1 / steps.length : 0
  );
  const progress = useSpring(progressTarget, SPRING);
  const [activeStep, setActiveStep] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    const update = () => {
      progressTarget.set(
        Math.min(1, Math.max(0, stepScrubberProgress(emblaApi, steps.length)))
      );
      setActiveStep(emblaApi.selectedScrollSnap());
      setScrollProgress(emblaApi.scrollProgress());
    };
    update();
    emblaApi.on("scroll", update);
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    return () => {
      emblaApi.off("scroll", update);
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
    };
  }, [emblaApi, progressTarget, steps.length]);

  // Layer beats aligned to carousel thirds (WEB-344 colour timing + glass sheets
  // between steps, matching desktop interleaved drops).
  const STEP_1 = 1 / 3;
  const STEP_2 = 2 / 3;
  const STEP_2_3_MID = 5 / 6;

  const glass0Y = useSpring(
    useTransform(progress, [STEP_1, STEP_1 + 0.08], [DROP, 0]),
    SPRING
  );
  const magentaY = useSpring(
    useTransform(progress, [STEP_1 + 0.05, STEP_1 + 0.18], [DROP, 0]),
    SPRING
  );
  const glass1Y = useSpring(
    useTransform(progress, [STEP_2 - 0.05, STEP_2 + 0.05], [DROP, 0]),
    SPRING
  );
  const blueY = useSpring(
    useTransform(
      progress,
      [STEP_2_3_MID - 0.07, STEP_2_3_MID + 0.07],
      [DROP, 0]
    ),
    SPRING
  );
  const glass2Y = useSpring(
    useTransform(
      progress,
      [STEP_2_3_MID + 0.02, STEP_2_3_MID + 0.12],
      [DROP, 0]
    ),
    SPRING
  );
  const boxVeil = useTransform(progress, [0.94, 1], [0, 1]);

  const colorLayers = [
    { variant: "orange" as const, bottom: LAYER_OFFSETS[0]!, z: 10, y: null },
    {
      variant: "magenta" as const,
      bottom: LAYER_OFFSETS[1]!,
      z: 20,
      y: magentaY,
    },
    { variant: "blue" as const, bottom: LAYER_OFFSETS[2]!, z: 30, y: blueY },
  ];

  const glassLayers = [
    { ...GLASS[0]!, y: glass0Y },
    { ...GLASS[1]!, y: glass1Y },
    { ...GLASS[2]!, y: glass2Y },
  ];

  const snaps = emblaApi?.scrollSnapList() ?? [];
  const snapSpan =
    snaps.length > 1 ? Math.max(snaps[1]! - snaps[0]!, 0.001) : 1;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <EngineHeading heading={heading} subheading={subheading} />
      <EnginePanel className="flex flex-col gap-8 px-4 py-8">
        {/* Layer stack — shared coordinate system like desktop visual. */}
        <div
          className="relative mx-auto w-full"
          style={{ height: STACK_H, maxWidth: STACK_W + 48 }}
        >
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2"
            style={{ width: STACK_W }}
          >
            {colorLayers.map((layer) => {
              const card = <UnderwritingEngineCard variant={layer.variant} />;
              const style = {
                width: STACK_W,
                left: 0,
                bottom: layer.bottom,
                zIndex: layer.z,
              } as const;
              return layer.y ? (
                <motion.div
                  key={layer.variant}
                  className="absolute will-change-transform"
                  style={{ ...style, y: layer.y }}
                >
                  {card}
                </motion.div>
              ) : (
                <div key={layer.variant} className="absolute" style={style}>
                  {card}
                </div>
              );
            })}

            {glassLayers.map((glass, i) => (
              <motion.div
                key={`glass-${i}`}
                className="absolute left-0 will-change-transform"
                style={{
                  width: STACK_W,
                  left: 0,
                  bottom: glass.bottom,
                  zIndex: glass.z,
                  y: glass.y,
                }}
              >
                <UnderwritingEngineGlass
                  variant={glass.variant}
                  tint={glass.tint}
                />
              </motion.div>
            ))}

            {/* Glass cube finisher — same placement as desktop (bottom: 3),
                scaled uniformly from top-left so rim + fill stay aligned. */}
            <div
              className="pointer-events-none absolute overflow-hidden"
              style={{
                left: 0,
                width: STACK_W,
                bottom: 3,
                height: BOX_H,
                zIndex: 38,
              }}
            >
              <UnderwritingEngineBox
                veilOpacity={boxVeil}
                style={{
                  width: BOX_NATIVE_W,
                  height: BOX_NATIVE_H,
                  transform: `scale(${SCALE})`,
                  transformOrigin: "top left",
                }}
              />
            </div>
          </div>
        </div>

        {/* Step carousel — horizontal slide with scaled next-card peek. */}
        <div className="overflow-hidden" ref={emblaRef}>
          <ol className="-ml-3 flex items-center">
            {steps.map((step, i) => {
              const snap = snaps[i] ?? i / Math.max(steps.length - 1, 1);
              const diff = emblaApi
                ? diffToSnap(snap, scrollProgress)
                : i - activeStep;
              const t = diff / snapSpan;
              const scale =
                PREVIEW_SCALE +
                (1 - PREVIEW_SCALE) * Math.max(0, 1 - Math.abs(t));
              const textOpacity = Math.max(0, Math.min(1, 1 - Math.abs(t) * 4));

              return (
                <li
                  key={step.number}
                  className="min-w-0 flex-[0_0_90%] pl-3"
                  aria-current={Math.abs(t) < 0.05 ? "step" : undefined}
                >
                  <article
                    className="flex min-h-[164px] origin-left flex-col gap-2 rounded-[8px] bg-white p-4 drop-shadow-[0px_4.827px_12.067px_rgba(0,0,0,0.06)] will-change-transform"
                    style={{
                      transform: `scale(${scale})`,
                    }}
                  >
                    <div
                      className="flex flex-col gap-2"
                      style={{ opacity: textOpacity }}
                      aria-hidden={textOpacity < 0.05 ? true : undefined}
                    >
                      <p className="text-grey-500 text-[12px] leading-[1.4]">
                        {step.eyebrow}
                      </p>
                      <p className="text-grey-950 text-[18px] leading-[1.3] tracking-[-0.02em]">
                        {step.title}
                      </p>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Progress scrubber — Figma 8883-25117: 288×36 container, 45 ticks. */}
        <Waveform
          progress={progress}
          className="mx-auto w-full max-w-[288px]"
          tickCount={MOBILE_TICK_COUNT}
          stepCount={steps.length}
          onStepSelect={(index) => emblaApi?.scrollTo(index)}
        />

        <p className="sr-only" aria-live="polite">
          Step {activeStep + 1} of {steps.length}: {steps[activeStep]?.eyebrow}
        </p>
      </EnginePanel>
    </div>
  );
}
